import React from "react";
import { getRedesignScenarios } from "../../services/redesignScenariosService";
import { parseEngineComposition, ENGINE_CHIP_STYLE } from "../../utils/redesignProvenance";

// AS-IS / TO-BE redesign scenarios (FBPM Ch.8) — READ-ONLY record.
// TO-BE models are produced by the AI agent under "Process Optimization" (and
// persisted when a version is saved). This tab only *displays* those records;
// it is not an editor.

const fmt = (x) =>
  x === null || x === undefined
    ? "—"
    : Number(x).toLocaleString(undefined, { maximumFractionDigits: 2 });

const card = {
  background: "var(--color-surface)",
  border: "1px solid var(--gray-200)",
  borderRadius: 10,
  padding: "14px 16px",
  marginBottom: 12,
};
const label = { fontSize: 11, color: "var(--gray-500)", fontWeight: 600 };

const METRICS = [
  { metric: "cycle_time_minutes", label: "Cycle time (min)" },
  { metric: "labor_cost", label: "Labor cost" },
  { metric: "cycle_time_efficiency", label: "CTE (%)" },
  { metric: "quality_score", label: "Quality (0–100)" },
  { metric: "flexibility_score", label: "Flexibility (0–100)" },
];

function Delta({ dim }) {
  if (!dim) return null;
  const improved = dim.improved;
  const color = improved === null ? "#6b7280" : improved ? "#059669" : "#dc2626";
  const arrow = improved === null ? "" : improved ? "▲" : "▼";
  return (
    <tr>
      <td style={{ padding: "6px 8px", fontSize: 13, color: "var(--gray-700)" }}>{dim.label}</td>
      <td style={{ padding: "6px 8px", fontSize: 13, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt(dim.as_is)}</td>
      <td style={{ padding: "6px 8px", fontSize: 13, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt(dim.to_be)}</td>
      <td style={{ padding: "6px 8px", fontSize: 13, textAlign: "right", color, fontWeight: 600, whiteSpace: "nowrap" }}>
        {dim.delta === null ? "—" : `${arrow} ${dim.percent_change === null ? fmt(dim.delta) : dim.percent_change + "%"}`}
      </td>
    </tr>
  );
}

function ComparisonTable({ comparison }) {
  if (!comparison) {
    return (
      <p style={{ fontSize: 12.5, color: "var(--gray-400)", margin: "6px 0" }}>
        No baseline linked.
      </p>
    );
  }
  const d = comparison.dimensions;
  return (
    <div style={{ overflowX: "auto" }}>
    <table style={{ borderCollapse: "collapse", width: "100%", marginTop: 6 }}>
      <thead>
        <tr>
          {["Dimension", "AS-IS", "TO-BE", "Change"].map((h, i) => (
            <th key={h} style={{ padding: "6px 8px", fontSize: 11, color: "var(--gray-500)", textAlign: i === 0 ? "left" : "right", borderBottom: "1px solid var(--gray-200)" }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <Delta dim={d.time} />
        <Delta dim={d.cost} />
        <Delta dim={comparison.cycle_time_efficiency} />
        <Delta dim={d.quality} />
        <Delta dim={d.flexibility} />
      </tbody>
    </table>
    </div>
  );
}

function Metrics({ s }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 10, margin: "8px 0" }}>
      {METRICS.map((f) => (
        <div key={f.metric}>
          <div style={label}>{f.label}</div>
          <div style={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {fmt(s.metrics[f.metric])}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Which engines produced this TO-BE — Agent alone, Agent+RL, Agent+BPR, or all three.
 *
 * This is the first thing a reader needs and the last thing they should have to hunt
 * for. Two cards with the same metrics and heuristic names are NOT interchangeable if
 * one came from RL and the other from the BPR model, so the composition renders as
 * colour-coded chips beside the title rather than as a clause inside the description.
 *
 * A record with no marker predates it: those saves were agent-only in fact, but the
 * chip says "unlabelled" rather than asserting a composition nobody recorded.
 */
function EngineComposition({ engines }) {
  if (!engines) {
    return (
      <span
        title="This scenario was saved before the engine composition was recorded. It predates RL/BPR composition, so it is the agent's own redesign."
        style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-400)", background: "var(--gray-100)", border: "1px solid var(--gray-200)", borderRadius: 999, padding: "2px 9px", cursor: "help" }}
      >
        engine not recorded
      </span>
    );
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
      <span style={{ fontSize: 10.5, fontWeight: 700, color: "var(--gray-500)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        Composed with
      </span>
      {engines.map((e, i) => (
        <React.Fragment key={e}>
          {i > 0 && <span style={{ fontSize: 11, color: "var(--gray-400)", fontWeight: 700 }}>+</span>}
          <span
            style={{
              fontSize: 11,
              fontWeight: 800,
              borderRadius: 999,
              padding: "2px 9px",
              whiteSpace: "nowrap",
              ...(ENGINE_CHIP_STYLE[e] || { color: "var(--gray-700)", background: "var(--gray-100)", border: "1px solid var(--gray-200)" }),
            }}
          >
            {e}
          </span>
        </React.Fragment>
      ))}
    </span>
  );
}

function Heuristics({ s }) {
  const list = s.applied_heuristics || [];
  if (list.length === 0) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ ...label, marginBottom: 4 }}>Applied heuristics</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {list.map((h) => (
          <div key={h.id} style={{ background: "var(--gray-50)", border: "1px solid var(--slate-100)", borderRadius: 8, padding: "6px 10px" }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{h.name}</span>
            {h.target_task_name && (
              <span style={{ fontSize: 12, color: "var(--gray-500)" }}> · on {h.target_task_name}</span>
            )}
            {h.note && <div style={{ fontSize: 12, color: "var(--gray-500)" }}>{h.note}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Read-only record of a process's AS-IS baseline(s) and the TO-BE redesigns the
 * AI agent produced, with the Before/After Devil's Quadrangle delta per TO-BE.
 */
export default function RedesignScenariosPanel({ processId, isActive }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    if (!processId) return;
    setLoading(true);
    setError("");
    try {
      setData(await getRedesignScenarios(processId));
      setLoaded(true);
    } catch (e) {
      setError(e.message || "Failed to load redesign scenarios");
    } finally {
      setLoading(false);
    }
  }, [processId]);

  React.useEffect(() => {
    const shouldLoad = isActive === undefined ? true : isActive;
    if (shouldLoad && processId && !loaded && !loading) load();
  }, [isActive, processId, loaded, loading, load]);

  const scenarios = (data && data.scenarios) || [];
  const asIsList = scenarios.filter((s) => s.kind === "AS_IS");
  const toBeList = scenarios.filter((s) => s.kind === "TO_BE");

  return (
    <div style={{ padding: "2px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Redesign Scenarios</h3>
        <span style={{ fontSize: 12, color: "var(--gray-500)" }}>
          AS-IS vs TO-BE (FBPM Ch.8 — Devil's Quadrangle)
        </span>
      </div>
      <div style={{ margin: "6px 0 12px", fontSize: 12, color: "var(--gray-500)" }}>
        A record of the current AS-IS baseline and the TO-BE redesigns produced by
        the AI agent, with the Before/After change across time, cost, quality and
        flexibility. TO-BE models are generated and saved under{" "}
        <strong>Process Optimization</strong> (AI Agents).
      </div>

      {loading && <p style={{ color: "var(--gray-500)" }}>Loading…</p>}
      {error && (
        <div style={{ margin: "10px 0", padding: "8px 12px", background: "var(--red-tint-50)", border: "1px solid var(--red-tint-200)", borderRadius: 8, color: "var(--red-ink-700)", fontSize: 13 }}>
          {error}
        </div>
      )}

      {loaded && scenarios.length === 0 && (
        <div style={{ ...card, background: "var(--gray-50)", color: "var(--gray-500)", fontSize: 13 }}>
          No redesign scenarios recorded yet. Run <strong>Process Optimization</strong>{" "}
          for this process and save a version — the AS-IS baseline and the TO-BE
          redesign will appear here.
        </div>
      )}

      {loaded && asIsList.length > 0 && (
        <div style={{ ...label, margin: "6px 0" }}>AS-IS BASELINES</div>
      )}
      {asIsList.map((s) => (
        <div key={s.id} style={{ ...card, borderLeft: "3px solid var(--gray-500)" }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{s.name}</div>
          {s.description && <div style={{ fontSize: 12.5, color: "var(--gray-500)" }}>{s.description}</div>}
          <Metrics s={s} />
        </div>
      ))}

      {loaded && toBeList.length > 0 && (
        <div style={{ ...label, margin: "14px 0 6px" }}>TO-BE REDESIGNS</div>
      )}
      {toBeList.map((s) => {
        // The marker is pulled OUT of the description so it renders once, as a chip —
        // leaving it in the prose too would just restate it in the place nobody reads.
        const { engines, rest } = parseEngineComposition(s.description);
        return (
        <div key={s.id} style={{ ...card, borderLeft: "3px solid #7c3aed" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{s.name}</div>
            <EngineComposition engines={engines} />
          </div>
          {rest && <div style={{ fontSize: 12.5, color: "var(--gray-500)", marginTop: 2 }}>{rest}</div>}
          <Metrics s={s} />
          <ComparisonTable comparison={s.comparison} />
          <Heuristics s={s} />
        </div>
        );
      })}
    </div>
  );
}
